import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import {
  Building,
  Receipt,
  FileText,
  Settings,
  Database,
  Save,
  Edit,
  X,
  Check,
  CheckCircle,
  Download
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { fetchSettingsFromAPI } from '../utils/settingsApi';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    companyDetails: {
      companyName: 'IDEAL TRENDZ INTERIORS',
      addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
      city: 'HYDERNAGAR',
      state: 'TELANGANA',
      pincode: '500072',
      contactNumber: '9014814901',
      emailId: 'idealtrendz.int@gmail.com',
      gstNumber: '36AHZPN3524A1Z9',
      panNumber: 'AHZPN3524A',
      bankName: 'HDFC Bank',
      accountHolderName: 'IDEAL TRENDZ INTERIORS',
      accountNumber: '50200012345678',
      ifscCode: 'HDFC0001234',
      branchName: 'Nizampet'
    },
    invoiceSettings: {
      prefix: 'INV-',
      startingNumber: 1201,
      taxRate: 18,
      discountRate: 10,
      currentNumber: 1201
    },
    estimateSettings: {
      prefix: 'EST-',
      startingNumber: 1001,
      validityDays: 30,
      currentNumber: 1001
    },
    closedOrderSettings: {
      prefix: 'CO-',
      startingNumber: 2001,
      currentNumber: 2001
    },
    employeeSettings: {
      idPrefix: 'EMP-',
      startingNumber: 1001,
      currentNumber: 1001
    },
    paymentsSettings: {
      overduePeriodDays: 30
    },
    aiBriefingSettings: {
      aiBriefingEnabled: false,
      aiBriefingFrequency: 'daily',
      aiBriefingTime: '09:00',
      aiBriefingPhone: ''
    }
  });

  const [originalSettings, setOriginalSettings] = useState({});

  // Global edit state - Default to read-only (false)
  const [isEditing, setIsEditing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [hasLocalData, setHasLocalData] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching settings from backend API...');

      // Use the utility function to fetch settings
      const mergedSettings = await fetchSettingsFromAPI();

      console.log('🔍 DEBUG: Merged settings received:', mergedSettings);
      console.log('🔍 DEBUG: Closed order settings:', mergedSettings.closedOrderSettings);

      // Ensure closedOrderSettings always exists
      const settingsWithDefaults = {
        ...mergedSettings,
        closedOrderSettings: mergedSettings.closedOrderSettings || {
          prefix: 'CO-',
          startingNumber: 2001,
          currentNumber: 2001
        },
        paymentsSettings: mergedSettings.paymentsSettings || {
          overduePeriodDays: mergedSettings.overduePeriodDays || 30
        },
        aiBriefingSettings: {
          aiBriefingEnabled: mergedSettings.aiBriefingEnabled || false,
          aiBriefingFrequency: mergedSettings.aiBriefingFrequency || 'daily',
          aiBriefingTime: mergedSettings.aiBriefingTime || '09:00',
          aiBriefingPhone: mergedSettings.aiBriefingPhone || ''
        }
      };

      console.log('🔍 DEBUG: Settings with defaults:', settingsWithDefaults.closedOrderSettings);

      setSettings(settingsWithDefaults);
      setOriginalSettings(mergedSettings);
      setHasLocalData(false); // We're using server data, not localStorage
      console.log('✅ All settings loaded successfully from server');

    } catch (error) {
      console.error('❌ Failed to fetch settings:', error);

      // Use complete default settings as fallback
      const defaultSettings = {
        companyDetails: {
          companyName: 'IDEAL TRENDZ INTERIORS',
          addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
          city: 'HYDERNAGAR',
          state: 'TELANGANA',
          pincode: '500072',
          contactNumber: '9014814901',
          emailId: 'idealtrendz.int@gmail.com',
          gstNumber: '36AHZPN3524A1Z9',
          panNumber: 'AHZPN3524A',
          bankName: 'HDFC Bank',
          accountHolderName: 'IDEAL TRENDZ INTERIORS',
          accountNumber: '50200012345678',
          ifscCode: 'HDFC0001234',
          branchName: 'Nizampet'
        },
        invoiceSettings: {
          prefix: 'INV-',
          startingNumber: 1201,
          taxRate: 18,
          discountRate: 10,
          currentNumber: 1201
        },
        estimateSettings: {
          prefix: 'EST-',
          startingNumber: 1001,
          validityDays: 30,
          currentNumber: 1001
        },
        closedOrderSettings: {
          prefix: 'CO-',
          startingNumber: 2001,
          currentNumber: 2001
        },
        systemSettings: {
          currency: 'INR',
          dateFormat: 'DD/MM/YYYY',
          backupFrequency: 'daily'
        }
      };

      setSettings(defaultSettings);
      setOriginalSettings(defaultSettings);
      setHasLocalData(false);
      console.warn('⚠️ Using default settings - API connection issue:', error.message);
      toast.error('Failed to connect to server. Using default settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    console.log('🔧 Input change:', { section, field, value });
    console.log('🔧 Previous settings for section:', settings[section]);

    setSettings(prev => {
      // Ensure the section exists before updating
      const currentSection = prev[section] || {};

      const newSettings = {
        ...prev,
        [section]: {
          ...currentSection,
          [field]: value
        }
      };
      console.log('🔧 New settings for section:', newSettings[section]);
      return newSettings;
    });
  };


  const saveAllSettings = async () => {
    setLoading(true);
    let savedCount = 0;
    let totalSections = 0;

    try {
      // Validate required fields before saving
      if (settings.closedOrderSettings?.prefix === '') {
        toast.error('Closed Order Prefix cannot be empty');
        setLoading(false);
        return;
      }

      if (settings.closedOrderSettings?.startingNumber === '' || settings.closedOrderSettings?.startingNumber < 1) {
        toast.error('Closed Order Starting Number must be a positive number');
        setLoading(false);
        return;
      }

      if (settings.paymentsSettings?.overduePeriodDays === '' || settings.paymentsSettings?.overduePeriodDays < 1) {
        toast.error('Overdue Period must be at least 1 day');
        setLoading(false);
        return;
      }

      // Add auth headers
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      console.log('💾 Saving all settings to backend API...');

      // Save company details and closed order settings to company-settings endpoint
      try {
        const companyData = {
          ...settings.companyDetails,
          closedOrderPrefix: settings.closedOrderSettings?.prefix || 'CO-',
          closedOrderStartingNumber: settings.closedOrderSettings?.startingNumber || 2001
        };

        console.log('📡 Saving company settings:', companyData);
        console.log('🔍 DEBUG: Current closedOrderSettings:', settings.closedOrderSettings);
        await axios.put('/admin/company-settings', companyData, { headers });
        console.log('✅ Company settings saved successfully');
        savedCount++;
      } catch (companyError) {
        console.error('❌ Failed to save company settings:', companyError.message);
        toast.error(`Failed to save company settings: ${companyError.response?.data?.error || companyError.message}`);
      }
      totalSections++;

      // Save other settings to legacy endpoint
      const sectionsToSave = ['invoiceSettings', 'estimateSettings', 'closedOrderSettings', 'employeeSettings', 'systemSettings', 'paymentsSettings'];

      for (const section of sectionsToSave) {
        totalSections++;
        if (settings[section]) {
          try {
            console.log(`📡 Saving ${section}:`, settings[section]);
            await axios.put('/admin/settings', {
              section,
              data: settings[section]
            }, { headers });
            console.log(`✅ ${section} saved successfully`);
            savedCount++;
          } catch (sectionError) {
            console.error(`❌ Failed to save ${section}:`, sectionError.message);
            toast.error(`Failed to save ${section}: ${sectionError.response?.data?.error || sectionError.message}`);
          }
        }
      }

      setIsEditing(false);
      setOriginalSettings(settings);

      // Show user-friendly message regardless of partial saves
      if (savedCount > 0) {
        toast.success('Settings updated successfully!');
        console.log(`✅ Settings saved: ${savedCount}/${totalSections} sections saved to backend`);
      } else {
        toast.error('Failed to save settings - please check your connection and try again');
        console.error('❌ No settings were saved to backend');
      }

    } catch (error) {
      console.error('❌ Unexpected error during save:', error);
      toast.error('An unexpected error occurred while saving settings');
    } finally {
      setLoading(false);
    }
  };

  const cancelAllEdits = () => {
    // Reset to original values
    setSettings(originalSettings);
    setIsEditing(false);
    toast('Changes cancelled', { icon: 'ℹ️' });
  };

  const clearLocalSettings = () => {
    localStorage.removeItem('adminSettings');
    localStorage.removeItem('adminSettingsTimestamp');
    localStorage.removeItem('adminSettingsSynced');
    localStorage.removeItem('adminSettingsLastSync');
    setHasLocalData(false);
    toast('Local settings cleared. Page will reload with server data.', { icon: 'ℹ️' });
    setTimeout(() => {window.location.reload();}, 1000);
  };


  const handleBackupData = () => {
    toast.success('Data backup initiated successfully!');
  };

  const handleRestoreData = () => {
    toast.success('Data restore completed successfully!');
  };

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-[#0A5F38]">Settings</h1>
              <p className="text-gray-600">Configure your shop settings and preferences</p>
              {loading && (
                <p className="text-sm text-[#0A5F38] mt-2">
                  🔄 Loading settings from server...
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-4">
              {isEditing ? (
                <>
                  <button
                    onClick={saveAllSettings}
                    disabled={loading}
                    className="flex items-center px-6 py-3 bg-[#0A5F38] text-white rounded-lg hover:bg-[#0A5F38]/90 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save All Settings'}
                  </button>
                  <button
                    onClick={cancelAllEdits}
                    disabled={loading}
                    className="flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-6 py-3 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E]"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Settings
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Company/Shop Details Settings */}
          <div className="card">
            <div className="flex items-center mb-6">
              <Building className="h-6 w-6 text-[#0A5F38] mr-3" />
              <h3 className="text-lg font-semibold text-[#0A5F38]">Company / Shop Details</h3>
            </div>

            {/* Company Details Form */}
            {settings.companyDetails && (
              <div className="space-y-6">
                {/* Company Information */}
                <div>
                  <h4 className="text-md font-semibold text-[#0A5F38] mb-4">Company Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.companyName || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'companyName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.companyName || 'Not set'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.contactNumber}
                          onChange={(e) => handleInputChange('companyDetails', 'contactNumber', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.contactNumber}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email ID</label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={settings.companyDetails?.emailId}
                          onChange={(e) => handleInputChange('companyDetails', 'emailId', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.emailId}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h4 className="text-md font-semibold text-[#0A5F38] mb-4">Address Information</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.addressLine1 || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'addressLine1', e.target.value)}
                          className="input-field"
                          placeholder="e.g. # Vertex Privilege, Villa No. 32, Nizampet Road"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.addressLine1 || 'Not set'}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2 <span className="text-gray-400 font-normal">(optional)</span></label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.addressLine2 || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'addressLine2', e.target.value)}
                          className="input-field"
                          placeholder="Apartment, suite, area, etc."
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.addressLine2 || '-'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.city || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'city', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.city}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.state || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'state', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.state}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.pincode || ''}
                          onChange={(e) => handleInputChange('companyDetails', 'pincode', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.pincode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tax Details */}
                <div>
                  <h4 className="text-md font-semibold text-[#0A5F38] mb-4">Tax Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.gstNumber}
                          onChange={(e) => handleInputChange('companyDetails', 'gstNumber', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.gstNumber}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.panNumber}
                          onChange={(e) => handleInputChange('companyDetails', 'panNumber', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.panNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </div>


                {/* Bank Details */}
                <div>
                  <h4 className="text-md font-semibold text-[#0A5F38] mb-4">Bank Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.bankName}
                          onChange={(e) => handleInputChange('companyDetails', 'bankName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.bankName}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.accountHolderName}
                          onChange={(e) => handleInputChange('companyDetails', 'accountHolderName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.accountHolderName}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.accountNumber}
                          onChange={(e) => handleInputChange('companyDetails', 'accountNumber', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.accountNumber}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.ifscCode}
                          onChange={(e) => handleInputChange('companyDetails', 'ifscCode', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.ifscCode}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Branch Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={settings.companyDetails?.branchName}
                          onChange={(e) => handleInputChange('companyDetails', 'branchName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                          {settings.companyDetails?.branchName}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Invoice Settings */}
            <div className="card">
              <div className="flex items-center mb-6">
                <Receipt className="h-6 w-6 text-[#0A5F38] mr-3" />
                <h3 className="text-lg font-semibold text-[#0A5F38]">Invoice Settings</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Prefix</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={settings.invoiceSettings?.prefix}
                        onChange={(e) => handleInputChange('invoiceSettings', 'prefix', e.target.value)}
                        className="input-field"
                        placeholder="SRRT"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.invoiceSettings?.prefix}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={settings.invoiceSettings?.startingNumber}
                        onChange={(e) => handleInputChange('invoiceSettings', 'startingNumber', parseInt(e.target.value))}
                        className="input-field"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.invoiceSettings?.startingNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Estimate Settings */}
            <div className="card">
              <div className="flex items-center mb-6">
                <FileText className="h-6 w-6 text-[#0A5F38] mr-3" />
                <h3 className="text-lg font-semibold text-[#0A5F38]">Estimate Settings</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimate Prefix</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={settings.estimateSettings?.prefix}
                        onChange={(e) => handleInputChange('estimateSettings', 'prefix', e.target.value)}
                        className="input-field"
                        placeholder="SRRT"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.estimateSettings?.prefix}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={settings.estimateSettings?.startingNumber}
                        onChange={(e) => handleInputChange('estimateSettings', 'startingNumber', parseInt(e.target.value))}
                        className="input-field"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.estimateSettings?.startingNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Closed Order Settings */}
            <div className="card">
              <div className="flex items-center mb-6">
                <CheckCircle className="h-6 w-6 text-[#C5A57D] mr-3" />
                <h3 className="text-lg font-semibold text-[#0A5F38]">Closed Order Settings</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Closed Order Prefix</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={settings.closedOrderSettings?.prefix ?? ''}
                        onChange={(e) => handleInputChange('closedOrderSettings', 'prefix', e.target.value)}
                        className="input-field"
                        placeholder="CO-"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.closedOrderSettings?.prefix || 'CO-'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={settings.closedOrderSettings?.startingNumber ?? ''}
                        onChange={(e) => handleInputChange('closedOrderSettings', 'startingNumber', e.target.value === '' ? '' : parseInt(e.target.value))}
                        className="input-field"
                        placeholder="2001"
                      />
                    ) : (
                      <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                        {settings.closedOrderSettings?.startingNumber || 2001}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Settings */}
          <div className="card mt-8">
            <div className="flex items-center mb-6">
              <Database className="h-6 w-6 text-[#C5A57D] mr-3" />
              <h3 className="text-lg font-semibold text-[#0A5F38]">Employee Settings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID Prefix</label>
                <p className="text-sm text-gray-500 mb-2">Prefix for auto-generated employee IDs</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={settings.employeeSettings?.idPrefix ?? 'EMP-'}
                    onChange={(e) => handleInputChange('employeeSettings', 'idPrefix', e.target.value)}
                    className="input-field"
                    placeholder="EMP-"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.employeeSettings?.idPrefix || 'EMP-'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                <p className="text-sm text-gray-500 mb-2">Starting number for employee ID sequence</p>
                {isEditing ? (
                  <input
                    type="number"
                    value={settings.employeeSettings?.startingNumber ?? 1001}
                    onChange={(e) => handleInputChange('employeeSettings', 'startingNumber', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="input-field"
                    placeholder="1001"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.employeeSettings?.startingNumber || 1001}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payments Settings */}
          <div className="card mt-8">
            <div className="flex items-center mb-6">
              <Settings className="h-6 w-6 text-[#C5A57D] mr-3" />
              <h3 className="text-lg font-semibold text-[#0A5F38]">Payments Settings</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Overdue Period (Days)</label>
                <p className="text-sm text-gray-500 mb-2">Number of days after invoice date before marking as overdue</p>
                {isEditing ? (
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={settings.paymentsSettings?.overduePeriodDays ?? ''}
                    onChange={(e) => handleInputChange('paymentsSettings', 'overduePeriodDays', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="input-field"
                    placeholder="30"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.paymentsSettings?.overduePeriodDays || 30} days
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* AI Executive Briefing Settings */}
          <div className="card mt-8">
            <div className="flex items-center mb-6">
              <Settings className="h-6 w-6 text-[#C5A57D] mr-3" />
              <h3 className="text-lg font-semibold text-[#0A5F38]">AI Executive Briefing</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enable AI Briefing</label>
                {isEditing ? (
                  <select
                    value={settings.aiBriefingSettings?.aiBriefingEnabled ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('aiBriefingSettings', 'aiBriefingEnabled', e.target.value === 'true')}
                    className="input-field"
                  >
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.aiBriefingSettings?.aiBriefingEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                {isEditing ? (
                  <select
                    value={settings.aiBriefingSettings?.aiBriefingFrequency || 'daily'}
                    onChange={(e) => handleInputChange('aiBriefingSettings', 'aiBriefingFrequency', e.target.value)}
                    className="input-field"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.aiBriefingSettings?.aiBriefingFrequency || 'daily'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time (HH:MM)</label>
                {isEditing ? (
                  <input
                    type="time"
                    value={settings.aiBriefingSettings?.aiBriefingTime || '09:00'}
                    onChange={(e) => handleInputChange('aiBriefingSettings', 'aiBriefingTime', e.target.value)}
                    className="input-field"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.aiBriefingSettings?.aiBriefingTime || '09:00'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={settings.aiBriefingSettings?.aiBriefingPhone || ''}
                    onChange={(e) => handleInputChange('aiBriefingSettings', 'aiBriefingPhone', e.target.value)}
                    className="input-field"
                    placeholder="e.g. 9876543210"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white border border-slate-100 rounded-lg text-slate-900">
                    {settings.aiBriefingSettings?.aiBriefingPhone || 'Not set'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;


